import React, { useEffect, useRef, useState } from 'react';

import { Box, Chip, Tooltip, Typography } from '@myn/mui';

import { type Approver } from './utils.js';

const MAX_WIDTH = 190;
type Props = {
  users: Approver[];
};

const UserList = ({ users }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleUsers, setVisibleUsers] = useState<Approver[]>(users);
  const [remainingUsers, setRemainingUsers] = useState<Approver[]>([]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const updateVisibleUsers = () => {
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const elements = Array.from(container.childNodes) as HTMLElement[];

      const { visible, hidden } = users.reduce(
        (
          acc: { total: number; visible: Approver[]; hidden: Approver[] },
          user,
          index,
        ) => {
          if (!elements[index]) {
            return acc;
          }

          if (acc.total >= MAX_WIDTH) {
            acc.hidden.push(user);
            return acc;
          }

          const operationWidth = user.operation ? 32 : 0;
          const totalElementWidth =
            elements[index].offsetWidth + operationWidth + 8;

          if (acc.total + totalElementWidth <= MAX_WIDTH) {
            acc.visible.push(user);
            acc.total = acc.total + totalElementWidth;
            return acc;
          }

          acc.total = acc.total + totalElementWidth;
          acc.hidden.push(user);

          return acc;
        },
        { visible: [], total: 0, hidden: [] },
      );

      setVisibleUsers(visible);
      setRemainingUsers(hidden);
    };

    const resizeObserver = new ResizeObserver(updateVisibleUsers);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [users]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: MAX_WIDTH,
        whiteSpace: 'nowrap',
      }}
    >
      {visibleUsers.map((user) => (
        <React.Fragment key={user.id}>
          <Typography variant="h6">{user.name}</Typography>
          {user.operation && (
            <Typography variant="caption02">{user.operation}</Typography>
          )}
        </React.Fragment>
      ))}
      {remainingUsers.length > 0 && (
        <Tooltip
          title={remainingUsers
            .map((u) => (u.operation ? `${u.name} ${u.operation}` : u.name))
            .join(' ')}
        >
          <Chip
            label={`+${remainingUsers.length}`}
            size="small"
            variant="outlined"
            color="default"
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default UserList;
